import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { toast } from '../lib/toast'
import type { User } from '../types'
import AppShell from '../components/AppShell'
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import ConfirmDialog from '../components/ui/ConfirmDialog'

export default function Settings() {
  const [user, setUser] = useState<User | null>(null)
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [newPw2, setNewPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const navigate = useNavigate()

  // Email change state
  const [newEmail, setNewEmail] = useState('')
  const [emailPw, setEmailPw] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)

  // Confirm dialog
  const [confirmKickDisconnect, setConfirmKickDisconnect] = useState(false)

  useEffect(() => {
    api<User>('/api/auth/me').then(setUser).catch((e: ApiError) => {
      if (e.status === 401) navigate('/login')
    })
  }, [])

  async function changePassword(e: React.FormEvent) {
    e.preventDefault()
    setMsg('')
    setErr('')
    if (newPw.length < 8) {
      setErr('Yeni şifre en az 8 karakter olmalı')
      return
    }
    if (newPw !== newPw2) {
      setErr('Yeni şifreler eşleşmiyor')
      return
    }
    setBusy(true)
    try {
      await api('/api/auth/me/password', {
        method: 'PATCH',
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      })
      setMsg('Şifre güncellendi')
      setCurrentPw('')
      setNewPw('')
      setNewPw2('')
    } catch (e) {
      setErr((e as Error).message || 'Hata')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-semibold">Hesap Ayarları</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">Email, şifre ve bağlantılarını yönet</p>
        </div>

        <Section title="Hesap">
          <div className="text-sm text-[var(--color-text)]">{user?.email}</div>
        </Section>

        <Section title="Şifre Değiştir">
          <form onSubmit={changePassword} className="space-y-3 max-w-md">
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-text-muted)]">Mevcut şifre</label>
              <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-text-muted)]">Yeni şifre</label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="En az 8 karakter" required />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-text-muted)]">Yeni şifre (tekrar)</label>
              <Input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} required />
            </div>
            {err && (
              <div className="text-xs px-3 py-2 rounded bg-[var(--color-danger)]/10 text-[var(--color-danger)] border border-[var(--color-danger)]/30">
                {err}
              </div>
            )}
            {msg && (
              <div className="text-xs px-3 py-2 rounded bg-[var(--color-success)]/10 text-[var(--color-success)] border border-[var(--color-success)]/30">
                {msg}
              </div>
            )}
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Güncelleniyor…' : 'Şifreyi Güncelle'}
            </Button>
          </form>
        </Section>

        <Section title="Email Değiştir">
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              setEmailBusy(true)
              try {
                await api('/api/auth/me/email/request', {
                  method: 'POST',
                  body: JSON.stringify({ new_email: newEmail, current_password: emailPw }),
                })
                toast.success('Onay maili gönderildi. Yeni email adresine gelen linke tıkla.')
                setNewEmail('')
                setEmailPw('')
              } catch (e) {
                toast.error((e as Error).message)
              } finally {
                setEmailBusy(false)
              }
            }}
            className="space-y-3 max-w-md"
          >
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-text-muted)]">Yeni email</label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-text-muted)]">Mevcut şifre</label>
              <Input type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} required />
            </div>
            <Button type="submit" variant="primary" disabled={emailBusy}>
              {emailBusy ? 'Gönderiliyor…' : 'Onay maili gönder'}
            </Button>
            <p className="text-xs text-[var(--color-text-muted)]">
              Yeni email'e onay linki gönderilir. Onayladığında 24 saat içinde aktif olur. Eski email
              adresine de bilgilendirme + iptal linki gider.
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">
              Şifreni hatırlamıyorsan önce <Link to="/forgot-password" className="hover:underline">şifremi unuttum</Link>{' '}
              akışıyla yeni şifre belirle.
            </p>
          </form>
        </Section>

        <Section title="Tehlikeli alan">
          <p className="text-sm text-[var(--color-text-muted)] mb-3">
            Kick bağlantını kesersen sahip olduğun kanallarda owner statün düşer (overlay'ler korunur).
            Yeniden bağlanmak için "Kanal Bağla" butonunu kullanabilirsin.
          </p>
          <Button variant="danger" onClick={() => setConfirmKickDisconnect(true)}>
            Kick bağlantımı kes
          </Button>
        </Section>
      </div>

      <ConfirmDialog
        open={confirmKickDisconnect}
        onClose={() => setConfirmKickDisconnect(false)}
        onConfirm={async () => {
          try {
            await api('/api/auth/me/kick', { method: 'DELETE' })
            toast.success('Kick bağlantısı kaldırıldı')
          } catch (e) {
            toast.error((e as Error).message)
          }
        }}
        title="Kick bağlantısını kes?"
        description="Sahip olduğun kanallarda owner statün düşer. Yeniden bağlamak için 'Kanal Bağla'yı kullan."
        confirmLabel="Kes"
        destructive
      />
    </AppShell>
  )
}
